import { deleteField, doc, getDoc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '~/config/firebase.config';
import { isMemorizedGame } from '~/config/gameParamsMemory';
import { ScreenStatus } from '~/types/screen';
import type { RegieState, RegieView, RosterPlayer } from '~/types/screen/regie';
import { Client } from '../Client';
import { ClientConverter } from './client.converter';

export class ClientDatabase {
    /**
     * Subscribe to client changes
     * @param clientID the client identifier
     * @returns a subscriber
     */
    static subscribe(clientID: string) {
        const ref = doc(db, 'clients', clientID);
        return (onValuehange: () => void) => onSnapshot(ref, onValuehange);
    }

    /**
     * Get a client from the firestore database
     * @param clientID the client identifier
     * @returns the corresponding client classe
     */
    static async getClient(clientID: string) {
        return ClientDatabase._getClientByID(clientID);
    }

    /**
     * Lit un feature flag du doc client (champ `features.<flag>`), en lecture
     * brute — le converter (partiel, gotcha #7) ne connaît pas `features`.
     * Absent, non-true ou erreur de lecture → false : un flag de rollout est opt-in.
     */
    static async getFeature(clientID: string, flag: string): Promise<boolean> {
        try {
            const snap = await getDoc(doc(db, 'clients', clientID));
            const features = snap.data()?.features;
            return features?.[flag] === true;
        } catch {
            return false;
        }
    }

    /**
     * Abonnement temps réel à un feature flag du doc client (`features.<flag>`),
     * en lecture brute (converter partiel — gotcha #7). C'est ce qui rend le
     * kill-switch de rollout effectif : couper le flag dans Firestore atteint
     * les écrans déjà lancés, sans redémarrage des bornes.
     */
    static subscribeFeature(clientID: string, flag: string, onChange: (enabled: boolean) => void) {
        const ref = doc(db, 'clients', clientID);
        return onSnapshot(ref, (snap) => {
            const features = snap.data()?.features;
            onChange(features?.[flag] === true);
        });
    }

    /**
     * Live subscription to a single client document, decoded via the converter.
     * Unlike `subscribe` (raw change notifier paired with a separate getSnapshot),
     * this delivers the converted client straight from the snapshot — used by
     * `useSyncClients` to keep the animator establishments list real-time.
     */
    static subscribeClient(clientID: string, onClient: (client: Client | undefined) => void) {
        const ref = doc(db, 'clients', clientID).withConverter(ClientConverter);
        return onSnapshot(ref, (snap) => onClient(snap.exists() ? snap.data() : undefined));
    }

    static async getClients(clientIDs: string[]) {
        const clients = await Promise.all(clientIDs.map((clientID) => ClientDatabase.getClient(clientID)));
        return clients;
    }

    /**
     * Marque le tutoriel d'onboarding comme terminé pour ce client.
     * updateDoc ciblé (jamais via toFirestore — gotcha #7 : converter partiel).
     */
    static completeOnboarding(clientID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            onboardingCompleted: true,
        });
    }

    static startScreenSession(clientID: string, screenID: string, sessionID: string, gameID: string) {
        updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.status`]: ScreenStatus.On,
            [`screenInfos.${screenID}.gameID`]: gameID,
            [`screenInfos.${screenID}.currentSessionID`]: sessionID,
        });
    }

    static stopScreenSession(clientID: string, screenID: string) {
        updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.status`]: ScreenStatus.Off,
            [`screenInfos.${screenID}.currentSessionID`]: null,
        });
    }

    static archiveScreen(clientID: string, screenID: string) {
        updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.status`]: ScreenStatus.Archive,
            [`screenInfos.${screenID}.gameID`]: null,
            [`screenInfos.${screenID}.instanceID`]: null,
        });
    }

    static unarchiveScreen(clientID: string, screenID: string) {
        updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.status`]: ScreenStatus.Off,
        });
    }

    static async renameScreen(clientID: string, screenID: string, name: string) {
        await updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.name`]: name,
        });
    }

    static changeGame(clientID: string, screenID: string, gameID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            // Lancer un jeu repart d'un état propre : QR géant, pause, veille ET
            // sourdine retirés — un nouveau jeu démarre avec le son (évite qu'un
            // Blind Test parte muet par oubli, irritant client #2).
            [`screenInfos.${screenID}.gameID`]: gameID,
            [`screenInfos.${screenID}.showQR`]: false,
            [`screenInfos.${screenID}.paused`]: false,
            [`screenInfos.${screenID}.idle`]: false,
            [`screenInfos.${screenID}.muted`]: false,
            // Réinitialise les paramètres : ils sont propres à un jeu, et certaines
            // clés sont partagées entre jeux avec des valeurs différentes (numRounds,
            // theme…). Sans reset, une valeur réglée sur le jeu précédent se reporterait
            // sur le nouveau (ex. thème geoloc relayé à draw-guessr → thème invalide).
            [`screenInfos.${screenID}.gameParams`]: null,
            // Double écran : distingue « Changer de jeu » de « Reprendre » (PublicStage). Additive.
            [`screenInfos.${screenID}.gameChangedAt`]: Date.now(),
        });
    }

    /**
     * Animator Control (menu) : l'écran est sur le menu de choix de jeu, aucun jeu
     * n'est chargé. On remet `gameID` à null pour que le cockpit reflète l'état
     * « menu » (tous les jeux redeviennent sélectionnables) au lieu d'afficher le
     * dernier jeu joué, périmé — `gameID` n'étant jamais remis à zéro autrement
     * (stopScreenSession désactivé). Le vrai gameID est ré-écrit par le collecteur
     * de stats (startScreenSession) dès qu'un jeu se lance. Additive field.
     */
    static clearScreenGame(clientID: string, screenID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.gameID`]: null,
            // Retour au menu = état propre, comme changeGame : sur le menu le cockpit
            // éteint et grise Pause / Son / Veille (la TV ne les écoute pas ; le QR, lui,
            // est écouté par le menu depuis console#227 et part de zéro ici). Un
            // drapeau resté actif du jeu précédent y serait invisible et impossible à
            // couper, puis s'appliquerait au jeu suivant lancé depuis la TV (partie
            // sous l'écran de veille, ou muette).
            [`screenInfos.${screenID}.showQR`]: false,
            [`screenInfos.${screenID}.paused`]: false,
            [`screenInfos.${screenID}.idle`]: false,
            [`screenInfos.${screenID}.muted`]: false,
        });
    }

    /**
     * Animator Control (V1.1) : commandes à distance appliquées en live par
     * l'écran (AnimatorScreenControls) — pause, son, écran de veille. On n'écrit
     * que les champs fournis (patch). Additive fields — jamais sur le chemin de jeu.
     */
    static setScreenControls(
        clientID: string,
        screenID: string,
        patch: { paused?: boolean; muted?: boolean; idle?: boolean }
    ) {
        const update: Record<string, boolean> = {};
        if (patch.paused !== undefined) update[`screenInfos.${screenID}.paused`] = patch.paused;
        if (patch.muted !== undefined) update[`screenInfos.${screenID}.muted`] = patch.muted;
        if (patch.idle !== undefined) update[`screenInfos.${screenID}.idle`] = patch.idle;
        return updateDoc(doc(db, 'clients', clientID), update);
    }

    /**
     * Animator Control (Phase 2A) : demande à l'écran de LANCER la partie du jeu
     * courant — l'équivalent à distance du bouton « Lancer » natif de chaque jeu.
     * On écrit un nonce horodaté (`launchRequest`) que l'écran relaie en un
     * `onMessage('launch')` dans l'iframe ; chaque jeu déclenche alors SA propre
     * fonction de démarrage (sa scène, ses gardes lobby). On sort aussi de veille /
     * pause pour ne pas lancer un jeu figé sous l'overlay. Le son (muted) reste tel
     * que réglé par l'animateur. Additive fields — jamais sur le chemin de jeu.
     */
    static requestLaunch(clientID: string, screenID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.launchRequest`]: Date.now(),
            [`screenInfos.${screenID}.idle`]: false,
            [`screenInfos.${screenID}.paused`]: false,
        });
    }

    /**
     * Animator Control (Phase 2D) : demande à l'écran de STOPPER la partie (retour à
     * l'écran lobby du jeu) — l'équivalent à distance du bouton « Arrêter » natif. On
     * écrit un nonce horodaté (`stopRequest`) que l'écran relaie en un
     * `onMessage('remote-stop')` dans l'iframe ; chaque jeu exécute alors SA propre
     * fonction de retour au lobby. Additive field — jamais sur le chemin de jeu.
     */
    static requestStop(clientID: string, screenID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.stopRequest`]: Date.now(),
        });
    }

    /**
     * Animator Control : commande de jeu générique envoyée PENDANT une partie
     * (ex. Unquiz : « question suivante » → message `show-next`). On écrit
     * {name, nonce} que l'écran relaie en un `onMessage(name)` dans l'iframe ;
     * le jeu exécute la commande s'il la comprend, sinon l'ignore.
     * Additive field — jamais lu sur le chemin de jeu.
     */
    static requestGameCommand(clientID: string, screenID: string, name: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.gameCommand`]: { name, nonce: Date.now() },
        });
    }

    /**
     * Animator Control (Phase 2B) : règle à distance les paramètres du jeu courant
     * (ex. Blind Test : playlist / nombre de chansons / mode de réponse). On écrit
     * l'objet complet ; l'écran le relaie au jeu ('set-game-params') qui applique ce
     * qu'il comprend, AVANT le lancement. Blob opaque — jamais lu sur le chemin de jeu.
     */
    static setGameParams(clientID: string, screenID: string, params: Record<string, unknown>) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.gameParams`]: params,
        });
    }

    /**
     * Jeu à réglages mémorisés (Blind Test, cf config/gameParamsMemory.ts) : écrit la
     * mémoire du jeu RÉGLAGE PAR RÉGLAGE (`changes`, `null` = retirer) — la TV et le
     * téléphone écrivent chacun de leur côté ; réécrire l'objet entier depuis un
     * instantané en retard annulerait le réglage que l'autre vient de changer (« le
     * dernier qui touche gagne » doit valoir réglage par réglage). `full` va aussi
     * dans `gameParams` pour une ancienne version de la console encore ouverte (`null`
     * = ne pas y toucher).
     */
    static setMemorizedGameParams(
        clientID: string,
        screenID: string,
        gameID: string,
        full: Record<string, unknown> | null,
        changes: Record<string, unknown>
    ) {
        if (!isMemorizedGame(gameID)) {
            // Jeu non mémorisé : seul `gameParams` existe, il faut l'objet complet.
            return full ? ClientDatabase.setGameParams(clientID, screenID, full) : Promise.resolve();
        }
        // `full` null : on ne touche pas à `gameParams` (écriture venue de la TV).
        const fields: Record<string, unknown> = full ? { [`screenInfos.${screenID}.gameParams`]: full } : {};
        for (const [key, value] of Object.entries(changes)) {
            fields[`screenInfos.${screenID}.gameParamsByGame.${gameID}.${key}`] =
                value === null ? deleteField() : value;
        }
        return updateDoc(doc(db, 'clients', clientID), fields);
    }

    /**
     * Met un quiz UnQuiz « à l'affiche » (armé) sans toucher aux autres
     * réglages du jeu : écriture par champ imbriqué, donc utilisable hors du
     * cockpit (studio) sans relire tout gameParams. Seulement si UnQuiz tourne
     * sur cet écran : sinon le lancement d'UnQuiz remettrait gameParams à zéro
     * (changeGame) et le quiz « à l'affiche » serait perdu en silence.
     * @returns false si UnQuiz n'est pas le jeu en cours sur l'écran.
     */
    static async armUnquizQuiz(clientID: string, screenID: string, quizId: string, quizTitle: string) {
        const snap = await getDoc(doc(db, 'clients', clientID));
        if (snap.data()?.screenInfos?.[screenID]?.gameID !== 'unquiz') return false;
        await updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.gameParams.quizId`]: quizId,
            [`screenInfos.${screenID}.gameParams.quizTitle`]: quizTitle,
        });
        return true;
    }

    /**
     * Animator Control : demande à l'écran d'afficher (ou masquer) le QR géant des
     * joueurs. L'écran écoute ce flag (QRCodeButton) et ouvre l'overlay GlobalPopup.
     * Additive field — jamais lu sur le chemin de jeu.
     */
    static setScreenShowQR(clientID: string, screenID: string, show: boolean) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.showQR`]: show,
        });
    }

    /**
     * Animator Control : battement de présence écrit par le cockpit animateur tant
     * qu'il pilote cet établissement. L'écran lit `animatorSeen` (présence fraîche
     * sur une fenêtre ~30s) pour masquer ses boutons opérateur pendant le pilotage
     * et les faire réapparaître seuls si l'appli disparaît. Additive field, server time.
     */
    static setScreenAnimatorPresence(clientID: string, screenID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.animatorSeen`]: serverTimestamp(),
        });
    }

    /**
     * Double écran « Régie » : la régie écrit ce que l'écran public affiche (vue,
     * nom de la soirée, bandeau, message), champ par champ — deux régies ouvertes
     * (ordinateur + téléphone) ne s'écrasent pas l'une l'autre. Additive.
     */
    static setRegie(clientID: string, screenID: string, patch: Partial<RegieState>) {
        const update: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(patch)) {
            if (value !== undefined) update[`screenInfos.${screenID}.regie.${key}`] = value;
        }
        if (Object.keys(update).length === 0) return Promise.resolve();
        update[`screenInfos.${screenID}.regie.updatedAt`] = Date.now();
        return updateDoc(doc(db, 'clients', clientID), update);
    }

    /**
     * Ce que montre l'écran public, en UNE écriture avec la pause : hors jeu (accueil,
     * message) la partie attend ; retour au jeu = elle repart. Écrire les deux à part
     * laissait le jeu repartir une fraction de seconde sous le calque.
     */
    static setRegieView(clientID: string, screenID: string, view: RegieView, extra: Partial<RegieState> = {}) {
        const update: Record<string, unknown> = {
            [`screenInfos.${screenID}.regie.view`]: view,
            [`screenInfos.${screenID}.regie.updatedAt`]: Date.now(),
            [`screenInfos.${screenID}.paused`]: view !== 'game',
        };
        for (const [key, value] of Object.entries(extra)) {
            if (value !== undefined) update[`screenInfos.${screenID}.regie.${key}`] = value;
        }
        return updateDoc(doc(db, 'clients', clientID), update);
    }

    /**
     * Joueurs connectés (pseudo, avatar), publiés par l'écran public pour la
     * régie. Écrit seulement quand la liste change. Additive.
     */
    static setScreenRoster(clientID: string, screenID: string, roster: RosterPlayer[]) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.roster`]: roster,
        });
    }

    static assignInstance(clientID: string, screenID: string, instanceID: string) {
        updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.instanceID`]: instanceID,
        });
    }

    /**
     * Live count of gamepads connected to a screen. Written (debounced) by the
     * screen app so the admin dashboard can tell "game loaded, 0 player" from
     * "actually being played". Additive field — never read on the gameplay path.
     */
    static setScreenPlayerCount(clientID: string, screenID: string, count: number) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.playerCount`]: count,
        });
    }

    /**
     * Presence heartbeat for screens that have no instance-level heartbeat
     * (anim/bar screens). Written periodically by the screen app so the admin
     * live dashboard can show them as online. Additive field, server time.
     */
    static setScreenLastSeen(clientID: string, screenID: string, onPhone = false) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.lastSeen`]: serverTimestamp(),
            // Qui bat : un téléphone ou une vraie TV. Deux horodatages plutôt qu'un
            // booléen partagé (sinon une TV + un téléphone ouverts ensemble se
            // l'arrachent à chaque battement) : la télécommande ne prévient « la TV
            // tourne sur un téléphone » que si AUCUNE vraie TV n'a battu. Additive.
            [`screenInfos.${screenID}.${onPhone ? 'phoneSeen' : 'tvSeen'}`]: serverTimestamp(),
        });
    }

    /**
     * Page TV sur TÉLÉPHONE passée en arrière-plan ou fermée : elle retire sa marque
     * `phoneSeen`, pour que la télécommande la voie hors ligne tout de suite au lieu
     * de 4 min plus tard (Le Cuba, 20/10/2026 ; cf Screen.isLive). Téléphones
     * seulement : `tvSeen` est partagé par TOUTES les pages non-téléphone (vraie TV,
     * portable du bureau, tablette…) — le retirer quand l'une se cache éteindrait la
     * vraie TV jusqu'à son battement suivant. `lastSeen` n'est pas touché (tableau
     * de bord admin).
     */
    static setScreenPhoneHidden(clientID: string, screenID: string) {
        return updateDoc(doc(db, 'clients', clientID), {
            [`screenInfos.${screenID}.hiddenAt`]: serverTimestamp(),
            [`screenInfos.${screenID}.phoneSeen`]: deleteField(),
        });
    }

    private static async _getClientByID(clientID: string) {
        const clientRef = doc(db, 'clients', clientID).withConverter(ClientConverter);
        const client = await getDoc(clientRef)
            .then((snap) => {
                if (snap.exists()) {
                    return snap.data();
                }
                throw new Error(`Client '${clientID}' does not exist.`);
            })
            .catch((err) => {
                throw new Error(`Cannot get client document with ref '/clients/${clientID}'\n[code=${err.code}]`);
            });
        return client;
    }
}
